import { ArrowRight } from 'lucide-react';
import { useInView } from '@/hooks/useInView';

const objections = [
  {
    objection: 'Most tools need months to deploy and a dedicated admin to run',
    counter: 'One OAuth click. No agents. No maintenance.',
    detail:
      'GitHub OAuth, select repos, see data. Auto-syncs continuously. No YAML, no infrastructure, no ongoing care required.',
  },
  {
    objection: 'Enterprise contracts and 6-month proof-of-concepts',
    counter: 'Free up to 10 developers. Self-serve.',
    detail:
      "No procurement team. No sales calls. Start today with your real data, upgrade when you're ready.",
  },
  {
    objection: 'Last time, my engineers revolted',
    counter: 'Team-level metrics only. Engineers request access.',
    detail:
      'No individual rankings. No screen monitoring. We surface the blockers frustrating your team \u2014 they want this data too.',
  },
  {
    objection: "Velocity dashboards that never explain the 'why'",
    counter: 'Knowledge silos. File hotspots. Real-time risk flags.',
    detail:
      'See which modules only one person understands. Catch rubber-stamped reviews and merged-without-approval PRs as they happen \u2014 not in a quarterly report.',
  },
];

const getCardDelay = (index: number) => {
  const delays = [
    'animation-delay-0',
    'animation-delay-100',
    'animation-delay-200',
    'animation-delay-300',
  ];
  return delays[index] || 'animation-delay-0';
};

export default function WhyDifferent() {
  const [sectionRef, isInView] = useInView<HTMLElement>({ threshold: 0.15 });

  return (
    <section ref={sectionRef} className="py-12 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div
            className={`text-center mb-10 ${
              isInView ? 'animate-fade-in-up' : 'opacity-0'
            }`}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Tried Engineering Analytics Before?
            </h2>
            <p className="text-gray-400 text-lg">
              Here&apos;s what we do differently.
            </p>
          </div>

          {/* Objection Cards */}
          <div className="grid md:grid-cols-2 gap-5">
            {objections.map((item, index) => (
              <div
                key={item.counter}
                className={`bg-gray-800/50 rounded-2xl p-6 border border-gray-700 ${
                  isInView
                    ? `animate-fade-in-up ${getCardDelay(index)}`
                    : 'opacity-0'
                }`}
              >
                <p className="text-sm text-gray-500 italic mb-2">
                  &ldquo;{item.objection}&rdquo;
                </p>
                <p className="text-lg font-bold text-white mb-2">
                  {item.counter}
                </p>
                <p className="text-sm text-gray-400 leading-relaxed">
                  {item.detail}
                </p>
              </div>
            ))}
          </div>

          {/* Closing Statement */}
          <div
            className={`text-center mt-8 pt-6 border-t border-gray-800 ${
              isInView ? 'animate-fade-in-up animation-delay-400' : 'opacity-0'
            }`}
          >
            <p className="inline-flex items-center gap-2 text-primary-400 font-medium text-lg">
              <span>See for yourself — connect GitHub and judge the data, not the pitch.</span>
              <ArrowRight className="w-5 h-5" />
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
